'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import { getSiteConfig, type SaferGamblingTimeOutPeriod } from '@shared/config';
import { useTranslations } from 'next-intl';
import { useEffect } from 'react';
import { FormProvider, useForm, type Resolver } from 'react-hook-form';
import { z } from 'zod';
import { MultistepForm } from '../../../../form-controls/MultistepForm/MultistepForm';
import { MyAccountSheet } from '../../../MyAccountSheet/MyAccountSheet';
import { DurationStep } from './steps/DurationStep';

type DurationType = SaferGamblingTimeOutPeriod;

type TimeOutFormData = {
  duration: SaferGamblingTimeOutPeriod | '';
  agreed: boolean;
};

type TimeOutSheetProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  trigger?: React.ReactNode;
};

function TimeOutSheet({ open, onOpenChange, trigger }: TimeOutSheetProps) {
  const t = useTranslations('my_account.safer_gambling.time_limits.time_out_sheet');
  const { timeOutPeriods, timeOutPeriodLabelKeys, preselectBreakToolPeriod, resetBreakToolFormOnClose } = getSiteConfig().SAFER_GAMBLING;

  const getDefaultValues = (): TimeOutFormData => ({
    duration: preselectBreakToolPeriod ? timeOutPeriods[0] : '',
    agreed: false,
  });

  const formSchema = z.object({
    duration: z.string().refine((value): value is SaferGamblingTimeOutPeriod => value !== '' && timeOutPeriods.includes(value as SaferGamblingTimeOutPeriod), {
      message: t('validation.duration_required'),
    }),
    agreed: z.boolean().refine(val => val === true, {
      message: t('validation.agreement_required'),
    }),
  });

  const methods = useForm<TimeOutFormData>({
    defaultValues: getDefaultValues(),
    resolver: zodResolver(formSchema) as Resolver<TimeOutFormData>,
  });

  useEffect(() => {
    if (!open && resetBreakToolFormOnClose) {
      methods.reset(getDefaultValues());
    }
  }, [open, resetBreakToolFormOnClose, preselectBreakToolPeriod, timeOutPeriods, methods]);

  const steps = [
    {
      id: 'duration',
      component: <DurationStep periods={timeOutPeriods} periodLabelKeys={timeOutPeriodLabelKeys} />,
      schema: formSchema,
    },
  ];

  return (
    <MyAccountSheet
      open={open}
      onOpenChange={onOpenChange}
      trigger={trigger}
      title={t('title')}
      contentClassName="max-w-md"
      data-track-feature="My Account"
      data-track-container="Timeout Set"
    >
      <FormProvider {...methods}>
        <MultistepForm steps={steps} showProgressBar={false} />
      </FormProvider>
    </MyAccountSheet>
  );
}

export { TimeOutSheet, type DurationType };
